import { Box, IconButton, Typography, Avatar } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CustomModal from '@/DesignSystem/components/custom-modal/CustomModal';
import { useEffect, useState, useRef, useCallback } from 'react';
import StoryContent from './StoryContent';
import { keyframes } from '@mui/system';

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const StoryModal = ({ open, story, onClose, onNext, onPrevious, hasNext, hasPrevious }) => {
    const [progress, setProgress] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [animationDuration, setAnimationDuration] = useState('5s');
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [displayStory, setDisplayStory] = useState(story);
    const touchStartX = useRef(null);
    const touchEndX = useRef(null);
    const progressBarRef = useRef(null);
    const timeoutRef = useRef(null);
    const transitionTimeoutRef = useRef(null);
    const duration = 5000; // 5 segundos
    const transitionDuration = 500; // Duración de la transición en ms (más lenta para ser más sutil)

    // Manejar transición entre stories
    useEffect(() => {
        if (!open || !story) {
            setDisplayStory(story);
            setIsTransitioning(false);
            return;
        }

        // Si la story cambió, iniciar transición
        if (story?.id !== displayStory?.id) {
            setIsTransitioning(true);

            // Después de la mitad de la transición, cambiar la story visible
            transitionTimeoutRef.current = setTimeout(() => {
                setDisplayStory(story);
                setIsTransitioning(false);
            }, transitionDuration / 2);
        }

        return () => {
            if (transitionTimeoutRef.current) {
                clearTimeout(transitionTimeoutRef.current);
                transitionTimeoutRef.current = null;
            }
        };
    }, [open, story, displayStory]);

    // Barra de progreso usando CSS transition para máxima fluidez
    useEffect(() => {
        if (!open || !displayStory || isTransitioning) {
            setProgress(0);
            setAnimationDuration('0s');
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            return;
        }

        // Resetear cuando cambia la story
        setProgress(0);
        setAnimationDuration('0s');

        // Pequeño delay para asegurar que el DOM esté listo y resetear el estado
        const startAnimation = () => {
            if (isPaused) {
                return;
            }

            setAnimationDuration(`${duration}ms`);
            // Forzar un re-render para que la transición comience desde 0
            requestAnimationFrame(() => {
                setProgress(100);
            });

            // Auto avanzar cuando termine la animación
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                if (hasNext) {
                    onNext();
                }
            }, duration);
        };

        // Doble requestAnimationFrame para asegurar que el reset se aplique primero
        requestAnimationFrame(() => {
            requestAnimationFrame(startAnimation);
        });

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [open, displayStory?.id, hasNext, onNext, isPaused, isTransitioning]);

    // Manejar pausa/reanudación
    useEffect(() => {
        if (!open || !displayStory || !progressBarRef.current) return;

        const progressBar = progressBarRef.current;

        if (isPaused) {
            // Pausar: obtener el progreso actual y guardarlo
            const computedStyle = window.getComputedStyle(progressBar);
            const currentWidth = parseFloat(computedStyle.width) || 0;
            const parentWidth = progressBar.parentElement?.offsetWidth || 100;
            const currentProgress = (currentWidth / parentWidth) * 100;

            // Guardar el progreso actual
            setProgress(currentProgress);

            // Cancelar el timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        } else if (progress < 100) {
            // Reanudar: continuar desde donde se pausó
            const remainingProgress = 100 - progress;
            const remainingTime = (remainingProgress / 100) * duration;

            setAnimationDuration(`${remainingTime}ms`);

            // Pequeño delay para que la transición se aplique correctamente
            requestAnimationFrame(() => {
                setProgress(100);
            });

            // Auto avanzar cuando termine la animación restante
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                if (hasNext) {
                    onNext();
                }
            }, remainingTime);
        }
    }, [isPaused, open, displayStory, progress, duration, hasNext, onNext]);

    // Handlers mejorados para navegación con transición
    const handleNext = useCallback(() => {
        if (isTransitioning) return;
        onNext();
    }, [isTransitioning, onNext]);

    const handlePrevious = useCallback(() => {
        if (isTransitioning) return;
        onPrevious();
    }, [isTransitioning, onPrevious]);

    const hasMovedRef = useRef(false);

    // Toggle pausa al hacer click/touch
    const handleContentClick = (e) => {
        // Solo pausar si no es un click en los botones de navegación
        if (e.target.closest('button') || e.target.closest('[role="button"]') || isTransitioning) {
            return;
        }
        setIsPaused((prev) => !prev);
    };

    // Detección de swipe
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        hasMovedRef.current = false;
        setIsPaused(true);
    };

    const handleTouchMove = (e) => {
        if (touchStartX.current !== null) {
            touchEndX.current = e.touches[0].clientX;
            hasMovedRef.current = true;
        }
    };

    const handleTouchEnd = () => {
        if (!touchStartX.current) {
            return;
        }

        if (!hasMovedRef.current || !touchEndX.current) {
            // Si no hubo movimiento, fue un tap - toggle pausa
            setIsPaused((prev) => !prev);
            touchStartX.current = null;
            touchEndX.current = null;
            hasMovedRef.current = false;
            return;
        }

        const distance = touchStartX.current - touchEndX.current;
        const minSwipeDistance = 50;

        if (distance > minSwipeDistance && hasNext && !isTransitioning) {
            // Swipe izquierda - siguiente
            handleNext();
        } else if (distance < -minSwipeDistance && hasPrevious && !isTransitioning) {
            // Swipe derecha - anterior
            handlePrevious();
        } else {
            // Si no fue un swipe, toggle pausa
            setIsPaused((prev) => !prev);
        }

        touchStartX.current = null;
        touchEndX.current = null;
        hasMovedRef.current = false;
    };

    // Detección de teclado
    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (e) => {
            if (e.key === 'ArrowRight' && hasNext && !isTransitioning) {
                handleNext();
            } else if (e.key === 'ArrowLeft' && hasPrevious && !isTransitioning) {
                handlePrevious();
            } else if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, hasNext, hasPrevious, handleNext, handlePrevious, onClose, isTransitioning]);

    if (!story) {
        return null;
    }

    const customHeader = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                position: 'relative',
                zIndex: 3,
                backgroundColor: 'background.default',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                {displayStory?.accountInfo?.image && (
                    <Avatar
                        src={displayStory.accountInfo.image}
                        alt={displayStory.accountInfo.nickname || 'Author'}
                        sx={{
                            width: 32,
                            height: 32,
                        }}
                    />
                )}
                <Typography variant="body2" fontWeight={500}>
                    {displayStory?.accountInfo?.nickname || 'Analítica Fantasy'}
                </Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ color: 'text.primary' }}>
                <CloseIcon />
            </IconButton>
        </Box>
    );

    return (
        <CustomModal
            open={open}
            onClose={onClose}
            hideHeader={true}
            hideFooter={true}
            customHeader={customHeader}
            width="90%"
            maxWidth={350}
            paperSx={{
                position: 'relative',
                backgroundColor: 'background.darkCard',
                height: '600px',
                maxHeight: '600px',
                display: 'flex',
                flexDirection: 'column',
            }}
            contentSx={{
                position: 'relative',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                p: 0,
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
            }}
        >
            {/* Barra de progreso - arriba del todo */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    zIndex: 4,
                }}
            >
                <Box
                    ref={progressBarRef}
                    sx={{
                        height: '100%',
                        width: `${progress}%`,
                        backgroundColor: '#2196F3',
                        willChange: 'width',
                        transition: isPaused ? 'none' : `width ${animationDuration} linear`,
                        animationPlayState: isPaused ? 'paused' : 'running',
                    }}
                />
            </Box>

            {/* Content con card con gradiente */}
            <Box
                onClick={handleContentClick}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    touchAction: 'pan-y',
                    flex: 1,
                    overflow: 'hidden',
                    mt: 6, // Espacio para el header
                    mx: 2,
                    mb: 2,
                    cursor: 'pointer',
                }}
            >
                {/* Contenedor de transición */}
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        minHeight: 'calc(700px - 120px)', // Altura del modal menos header y márgenes
                        height: isTransitioning ? 'auto' : 'auto',
                    }}
                >
                    {/* Story actual */}
                    {displayStory && (
                        <Box
                            sx={{
                                position: isTransitioning ? 'absolute' : 'relative',
                                top: 0,
                                left: 0,
                                right: 0,
                                width: '100%',
                                minHeight: isTransitioning ? 'calc(700px - 120px)' : 'auto',
                                zIndex: isTransitioning ? 1 : 2,
                                animation: isTransitioning
                                    ? `${fadeOut} ${transitionDuration}ms ease-in-out forwards`
                                    : 'none',
                            }}
                        >
                            <StoryContent story={displayStory} showImage={true} />
                        </Box>
                    )}

                    {/* Story siguiente/anterior (durante transición) */}
                    {isTransitioning && story && story.id !== displayStory?.id && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                width: '100%',
                                minHeight: 'calc(700px - 120px)',
                                zIndex: 2,
                                animation: `${fadeIn} ${transitionDuration}ms ease-in-out forwards`,
                            }}
                        >
                            <StoryContent story={story} showImage={true} />
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Navegación */}
            {hasPrevious && (
                <IconButton
                    onClick={handlePrevious}
                    disabled={isTransitioning}
                    sx={{
                        position: 'absolute',
                        left: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        zIndex: 3,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                        '&:disabled': {
                            opacity: 0.5,
                        },
                    }}
                >
                    <ChevronLeftIcon />
                </IconButton>
            )}

            {hasNext && (
                <IconButton
                    onClick={handleNext}
                    disabled={isTransitioning}
                    sx={{
                        position: 'absolute',
                        right: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        zIndex: 3,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                        '&:disabled': {
                            opacity: 0.5,
                        },
                    }}
                >
                    <ChevronRightIcon />
                </IconButton>
            )}
        </CustomModal>
    );
};

export default StoryModal;
